import { resetAuthNotificationDebouncers } from "./notificationDebouncer";

interface LogoutSession {
    id: string;
    initiated: boolean;
    navigationStarted: boolean;
    navigationCompleted: boolean;
    startTime: number;
    reason?: string;
}

// Track current logout session
let currentLogoutSession: LogoutSession | null = null;

/**
 * Create a new logout success tracker
 */
export const createLogoutSuccessTracker = (reason?: string) => {
    const sessionId = `logout_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    currentLogoutSession = {
        id: sessionId,
        initiated: false,
        navigationStarted: false,
        navigationCompleted: false,
        startTime: Date.now(),
        reason
    };
    
    console.log(`🎯 Created logout tracker: ${sessionId}`, { reason });
    
    return {
        sessionId,
        
        markLogoutStarted: () => {
            if (currentLogoutSession?.id === sessionId) {
                currentLogoutSession.initiated = true;
                console.log(`🚀 Logout initiated: ${sessionId}`);
            }
        },
        
        markNavigationStarted: () => {
            if (currentLogoutSession?.id === sessionId) {
                currentLogoutSession.navigationStarted = true;
                console.log(`🧭 Logout navigation started: ${sessionId}`);
            }
        },
        
        markNavigationCompleted: () => {
            if (currentLogoutSession?.id === sessionId) {
                currentLogoutSession.navigationCompleted = true;
                const duration = Date.now() - currentLogoutSession.startTime;
                console.log(`✅ Logout navigation completed: ${sessionId} (${duration}ms)`);
                
                // Reset auth notification debouncers after successful logout
                resetAuthNotificationDebouncers();
                
                // Clean up after a delay
                setTimeout(() => {
                    if (currentLogoutSession?.id === sessionId) {
                        currentLogoutSession = null;
                        console.log(`🧹 Cleaned up logout session: ${sessionId}`);
                    }
                }, 2000);
            }
        },
        
        wasSuccessful: () => {
            return currentLogoutSession?.id === sessionId && 
                   currentLogoutSession.initiated && 
                   currentLogoutSession.navigationCompleted;
        },
        
        getStatus: () => {
            if (currentLogoutSession?.id !== sessionId) {
                return 'expired';
            }
            
            if (currentLogoutSession.navigationCompleted) {
                return 'completed';
            }
            
            if (currentLogoutSession.navigationStarted) {
                return 'navigating';
            }
            
            if (currentLogoutSession.initiated) {
                return 'initiated';
            }
            
            return 'created';
        }
    };
};

/**
 * Check if there's currently a logout in progress
 */
export const isLogoutInProgress = (): boolean => {
    return currentLogoutSession !== null && 
           currentLogoutSession.initiated && 
           !currentLogoutSession.navigationCompleted;
};

/**
 * Get current logout session info (for debugging)
 */
export const getCurrentLogoutSession = () => {
    return currentLogoutSession ? { ...currentLogoutSession } : null;
};

/**
 * Force cleanup of current logout session (useful for error recovery)
 */
export const forceCleanupLogoutSession = () => {
    if (currentLogoutSession) {
        const sessionId = currentLogoutSession.id;
        currentLogoutSession = null;
        console.log(`🧹 Force cleaned up logout session: ${sessionId}`);
    }
};

/**
 * Enhanced logout function that tracks success
 * This should be called from useAuthActions
 */
export const trackLogoutNavigation = () => {
    if (currentLogoutSession && !currentLogoutSession.navigationCompleted) {
        currentLogoutSession.navigationStarted = true;
        
        // Listen for navigation completion
        const handleNavigation = () => {
            if (currentLogoutSession && window.location.pathname.includes('/login')) {
                currentLogoutSession.navigationCompleted = true;
                resetAuthNotificationDebouncers();
                console.log(`✅ Logout navigation completed via URL change`);
                
                // Remove listener
                window.removeEventListener('popstate', handleNavigation);
            }
        };
        
        // Monitor URL changes
        window.addEventListener('popstate', handleNavigation);
        
        // Also check after a short delay (for programmatic navigation)
        setTimeout(() => {
            handleNavigation();
        }, 100);
    }
};
